'use client';

import { useEffect, useState, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import type { ImagePrimitive, AmbientVideoPrimitive } from '@fshr/shared/types';

import Image from '../image';
import AmbientVideo from '../ambient-video';
import BevSafeText from '../bev-safe-text';
import Breadcrumb from '../breadcrumb';
import type { BreadcrumbItem } from '../breadcrumb';

export type Props = {
	title: string;
	subheading?: string;
	image: ImagePrimitive;
	desktopVideo?: AmbientVideoPrimitive;
	mobileVideo?: AmbientVideoPrimitive;
	/** Breadcrumb trail rendered above the title (PDP use — IAI-271). */
	breadcrumb?: BreadcrumbItem[];
	className?: string;
};

// Shared with the <hgroup> so the breadcrumb's left edge lines up with the
// centred title block.
const CONTAINER_WIDTH =
	'mx-auto w-full max-w-[var(--wide-grid-container-width-sm)] md:max-w-[var(--wide-grid-container-width-md)] lg:max-w-[var(--wide-grid-container-width-lg)] xl:max-w-[var(--wide-grid-container-width-xl)]';

export default function EditorialHero({
	title,
	subheading,
	image,
	desktopVideo,
	mobileVideo,
	breadcrumb,
	className,
}: Props): ReactElement {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

	useEffect(() => {
		setPrefersReducedMotion(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
	}, []);

	return (
		<section className={cn('spacing-stack-l flex flex-col', className)}>
			<div className="spacing-wide-grid w-full">
				<div
					className={cn(
						CONTAINER_WIDTH,
						'flex flex-col',
						// With a breadcrumb, spacing-stack-xl (30/40/50) is the gap down to
						// the title; the breadcrumb brings its own card-padding-s top inset.
						// Without one, the <hgroup>'s own top padding is that inset.
						breadcrumb?.length && 'spacing-stack-xl'
					)}
				>
					{breadcrumb?.length ? <Breadcrumb items={breadcrumb} fullWidth /> : null}
					<hgroup
						className={cn(
							'flex flex-col items-center text-center',
							!breadcrumb?.length && 'pt-spacing-card-padding-m'
						)}
					>
						<h1
							className={`text-display2 text-text-heading capitalize${
								subheading ? ' spacing-heading-s' : ''
							}`}
						>
							<BevSafeText>{title}</BevSafeText>
						</h1>
						{subheading && (
							<p className="text-h3 text-text-primary max-w-[640px]">
								<BevSafeText>{subheading}</BevSafeText>
							</p>
						)}
					</hgroup>
				</div>
			</div>
			<div className="spacing-wide-grid w-full">
				<figure className="relative isolate overflow-hidden">
					<div className="pt-[100%] lg:pt-[56.25%]" />
					<div className="absolute inset-0">
						<Image
							roughSize="l"
							imagePrimitive={image}
							alt={image.alt}
							className="h-full w-full object-cover"
						/>
						<AmbientVideo
							desktop={desktopVideo}
							mobile={mobileVideo}
							autoplay={!prefersReducedMotion}
							toggleClassName="bottom-150 left-auto right-150 size-200 md:bottom-300 md:left-300 md:right-auto md:size-400"
							toggleIconClassName="text-[20px] md:text-[40px]"
						/>
					</div>
				</figure>
			</div>
		</section>
	);
}
